import { pathHasWildcardOnlyAtSegmentStartOrMiddle } from '@terra/shared-asset-processing';
import { DomainAuthority } from './domain-authority';

const DOMAIN_PATH_PATTERN = /^(\/[a-zA-Z0-9\-_.*?]*)*\/?$/;
const PROTOCOL_REGEX = /^(https?:\/\/)/i;
const APPLICATION_ORIGIN_PATTERN = /^(https?:\/\/[^/?#]+)/i;

const applicationUrlHasQueryOrHash = (url: string): boolean =>
  url.includes('?') || url.includes('#');

export type ApplicationUrlValidationError =
  | 'invalid'
  | 'invalidTrailingCharacter';

const applicationUrlHasInvalidTrailingCharacter = (url: string): boolean =>
  url.endsWith('/') || url.endsWith('*');

export const getApplicationUrlValidationError = (
  url: string,
): ApplicationUrlValidationError | null => {
  const trimmed = url.trim();

  if (!trimmed || !PROTOCOL_REGEX.test(trimmed)) {
    return 'invalid';
  }

  if (applicationUrlHasInvalidTrailingCharacter(trimmed)) {
    return 'invalidTrailingCharacter';
  }

  try {
    return new URL(trimmed).hostname ? null : 'invalid';
  } catch {
    return 'invalid';
  }
};

export const isValidApplicationOriginUrl = (url: string): boolean =>
  getApplicationUrlValidationError(url) === null;

export const isValidApplicationUrl = (url: string): boolean =>
  getApplicationUrlValidationError(url) === null;

export const toApplicationOriginUrl = (url: string): string | null => {
  const trimmed = url.trim();

  if (!trimmed || !PROTOCOL_REGEX.test(trimmed)) {
    return null;
  }

  if (applicationUrlHasQueryOrHash(trimmed)) {
    return null;
  }

  const originMatch = trimmed.match(APPLICATION_ORIGIN_PATTERN);

  if (!originMatch) {
    return null;
  }

  return `${originMatch[1]}/`;
};

export const getApplicationUrlScopePath = (url: string): string | null => {
  const trimmed = url.trim();

  if (!trimmed || !PROTOCOL_REGEX.test(trimmed)) {
    return null;
  }

  if (applicationUrlHasQueryOrHash(trimmed)) {
    return null;
  }

  try {
    const scopePath = normalizeScopePathForComparison(
      normalizeScopePathInput(new URL(trimmed).pathname),
    );

    if (!isValidScopePath(scopePath)) {
      return null;
    }

    return scopePath;
  } catch {
    return null;
  }
};

export const normalizeScopePathInput = (path: string): string => {
  const trimmed = path.trim();

  if (!trimmed || trimmed.startsWith('/')) {
    return trimmed;
  }

  return `/${trimmed}`;
};

const pathnamePortionOfScopePath = (path: string): string =>
  path.split(/[?#]/)[0];

export const normalizeScopePathForComparison = (path: string): string => {
  const normalized = normalizeScopePathInput(path.trim());
  const pathnamePortion = pathnamePortionOfScopePath(normalized);
  const suffix = normalized.slice(pathnamePortion.length);
  const normalizedPathname =
    pathnamePortion.replace(/\/+$/, '') || pathnamePortion;

  return `${normalizedPathname}${suffix}`;
};

export const isValidDomainHost = (domain: string): boolean =>
  DomainAuthority.isValid(domain);

export const isValidDomainPath = (path: string): boolean => {
  if (!path.startsWith('/')) {
    return false;
  }

  const pathnamePortion = pathnamePortionOfScopePath(path);

  if (pathnamePortion.includes('*')) {
    if (!pathHasWildcardOnlyAtSegmentStartOrMiddle(pathnamePortion)) {
      return false;
    }
  }

  if (path.includes('?') || path.includes('#')) {
    return pathnamePortion === '/' || DOMAIN_PATH_PATTERN.test(pathnamePortion);
  }

  return (
    pathHasWildcardOnlyAtSegmentStartOrMiddle(path) &&
    DOMAIN_PATH_PATTERN.test(path)
  );
};

export const isValidScopePath = (path: string): boolean => {
  if (!isValidDomainPath(path)) {
    return false;
  }

  if (path.includes('?') || path.includes('#')) {
    return path.length > 1;
  }

  return path.replace(/\/+$/, '').length > 1;
};

const SCOPE_PATH_PATHNAME_CHAR_PATTERN = /^[a-zA-Z0-9/\-_.*?]*$/;
const SCOPE_PATH_SUFFIX_CHAR_PATTERN = /^[?#a-zA-Z0-9/=_.\-&]*$/;

export type ScopePathInputValidationError =
  | 'containsSpaces'
  | 'invalidCharacters'
  | 'invalidStructure';

export const getScopePathInputValidationError = (
  path: string,
): ScopePathInputValidationError | null => {
  const trimmed = path.trim();

  if (!trimmed) {
    return null;
  }

  if (/\s/.test(trimmed)) {
    return 'containsSpaces';
  }

  const pathnamePortion = pathnamePortionOfScopePath(trimmed);
  const suffix = trimmed.slice(pathnamePortion.length);

  if (!SCOPE_PATH_PATHNAME_CHAR_PATTERN.test(pathnamePortion)) {
    return 'invalidCharacters';
  }

  if (suffix && !SCOPE_PATH_SUFFIX_CHAR_PATTERN.test(suffix)) {
    return 'invalidCharacters';
  }

  const normalizedPath = normalizeScopePathInput(trimmed);

  if (!isValidScopePath(normalizedPath)) {
    return 'invalidStructure';
  }

  return null;
};

export const isValidDomain = (domainWithPath: string): boolean => {
  const parts = domainWithPath.split('/');
  const domain = parts[0];
  const path = parts.length > 1 ? `/${parts.slice(1).join('/')}` : null;

  if (!isValidDomainHost(domain)) {
    return false;
  }

  if (!path) {
    return true;
  }

  return isValidDomainPath(path);
};
